import { __extends, __awaiter, __generator, __assign, __spreadArray } from 'tslib';
import { KpkApiFormType, KpkApiFormFieldType, KpkApiResponseUtils, postMedia, postUserSelection, postUserSelectionMediasCollection, postUserSelectionBulk, getUserSelectionBulkFields, addMediaToFolder, postClassificationValidationRequest } from '@keepeek/api-client';
import { ImportStatus } from '@keepeek/commons';
import Axios from 'axios';
import axiosRetry from 'axios-retry';
import { IndexationStep } from './atoms.js';
import { ClassificationDestinationType } from './types.js';

var MAX_INDEXATION_RETRY = 20;
/**
 * this extension mapping has been retrieved from the "media_type" table of the backoffice while waiting for this information to be available on the API side:
 */
var PICTURE_EXTENSIONS = [
    "ai",
    "art",
    "bay",
    "bmp",
    "cgm",
    "cin",
    "cmyk",
    "cr2",
    "crw",
    "cur",
    "cut",
    "dc2",
    "dcm",
    "dcr",
    "dcx",
    "dib",
    "dng",
    "dot",
    "dpx",
    "emf",
    "epdf",
    "epi",
    "eps",
    "eps2",
    "eps3",
    "epsf",
    "epsi",
    "ept",
    "fax",
    "fits",
    "fix",
    "fpx",
    "gif",
    "gplt",
    "gray",
    "hpgl",
    "ico",
    "info",
    "jbig",
    "jng",
    "jp2",
    "jpc",
    "jpeg",
    "jpg",
    "kdc",
    "man",
    "mat",
    "miff",
    "mng",
    "mono",
    "mpc",
    "mrw",
    "nef",
    "palm",
    "pbm",
    "pcd",
    "pcl",
    "pcx",
    "pef",
    "pgm",
    "pict",
    "png",
    "ps",
    "psd",
    "raf",
    "raw",
    "rla",
    "rle",
    "svg",
    "tga",
    "tif",
    "tiff",
    "wmf",
    "x3f",
];
var DOCUMENT_EXTENSIONS = [
    "doc",
    "docx",
    "htm",
    "html",
    "odp",
    "ods",
    "odt",
    "pdf",
    "pot",
    "potx",
    "pps",
    "ppt",
    "pptx",
    "rtf",
    "txt",
    "xls",
    "xlsx",
];
var VIDEO_EXTENSIONS = [
    "avi",
    "divx",
    "flv",
    "m2ts",
    "m4v",
    "mkv",
    "mov",
    "mp4",
    "mpeg",
    "mpg",
    "mts",
    "vcr",
    "vob",
    "webm",
    "wmv",
];
var SOUND_EXTENSIONS = ["extension", "flac", "mp3", "wav"];
function getFormTypeFromMediaImport(kpkMediaImport) {
    if (!kpkMediaImport ||
        !kpkMediaImport.originalFileName ||
        !kpkMediaImport.originalFileName.includes(".")) {
        return null;
    }
    var mediaExtension = kpkMediaImport.originalFileName
        .substring(kpkMediaImport.originalFileName.lastIndexOf(".") + 1)
        .toLowerCase();
    if (PICTURE_EXTENSIONS.includes(mediaExtension)) {
        return KpkApiFormType.Picture;
    }
    else if (VIDEO_EXTENSIONS.includes(mediaExtension)) {
        return KpkApiFormType.Video;
    }
    else if (SOUND_EXTENSIONS.includes(mediaExtension)) {
        return KpkApiFormType.Sound;
    }
    else if (DOCUMENT_EXTENSIONS.includes(mediaExtension)) {
        return KpkApiFormType.Document;
    }
    else {
        return KpkApiFormType.Other;
    }
}
function buildMetadataForIndexation(formFields) {
    var ret = {
        metadata: []
    };
    formFields.forEach(function (formField) {
        if (formField.value) {
            switch (formField.type) {
                case KpkApiFormFieldType.CheckBox:
                    ret.metadata.push({
                        id: formField.id,
                        value: formField.value === "true"
                    });
                    break;
                case KpkApiFormFieldType.Number:
                    ret.metadata.push({
                        id: formField.id,
                        value: Number(formField.value)
                    });
                    break;
                case KpkApiFormFieldType.Thesaurus:
                    if (formField.value && Array.isArray(formField.value) && formField.value.length > 0) {
                        ret.metadata.push({
                            id: formField.id,
                            _embedded: {
                                value: formField.value.map(function (v) {
                                    var thesaurusId = Number(v.id);
                                    // if thesaurus id is negative => it's a candidate term :
                                    if (thesaurusId < 0) {
                                        return {
                                            title: v.label
                                        };
                                    }
                                    else {
                                        return {
                                            id: thesaurusId
                                        };
                                    }
                                })
                            }
                        });
                    }
                    break;
                default:
                    if ((Array.isArray(formField.value) && formField.value.length) ||
                        (!Array.isArray(formField.value) && formField.value)) {
                        ret.metadata.push({
                            id: formField.id,
                            value: Array.isArray(formField.value) ? formField.value.join(",") : formField.value
                        });
                    }
            }
        }
    });
    return ret;
}
var IndexationException = /** @class */ (function (_super) {
    __extends(IndexationException, _super);
    function IndexationException(message) {
        var _this = _super.call(this, message) || this;
        _this.name = "IndexationException";
        return _this;
    }
    return IndexationException;
}(Error));
var createCustomIndexationRetryAxiosInstance = function () {
    var instance = Axios.create();
    instance.interceptors.request.use(function (config) {
        if (!config.headers) {
            config.headers = {};
        }
        config.headers.Authorization = Axios.defaults.headers.common.Authorization;
        return config;
    });
    axiosRetry(instance, {
        retries: MAX_INDEXATION_RETRY,
        retryDelay: function (retryNumber, axiosError) {
            console.info("detect network error : let's retry request... [retryNumber=".concat(retryNumber, "/").concat(MAX_INDEXATION_RETRY, "] [URL=").concat(axiosError.config.url, "] [Method=").concat(axiosError.config.method, "]"), axiosError.toJSON());
            return axiosRetry.exponentialDelay(retryNumber) + 2000;
        },
        retryCondition: function (error) {
            var _a, _b, _c;
            // detect network connection issues
            var url = ((_a = error.config.url) === null || _a === void 0 ? void 0 : _a.startsWith("/")) ? error.config.url : "/".concat(error.config.url);
            return ((((_b = error.config.method) === null || _b === void 0 ? void 0 : _b.toLowerCase()) === "post" &&
                ((url.startsWith("/api/dam/medias") && error.message === "Network Error") ||
                    url.startsWith("/api/dam/users") ||
                    url.startsWith("/api/dam/folders") ||
                    url.startsWith("/api/dam/classification-validation-requests")) &&
                // network cut
                (error.message === "Network Error" ||
                    // exchange with the API while the token is temporarily no longer valid
                    (((_c = error.response) === null || _c === void 0 ? void 0 : _c.status) === 401 && error.response.data.message === "Unauthorized"))) ||
                false);
        }
    });
    return instance;
};
function customPostUserSelectionBulk(axiosInstance, metadata, selectionId, userId) {
    return __awaiter(this, void 0, void 0, function () {
        var response;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, postUserSelectionBulk(axiosInstance, __assign(__assign({}, metadata), { selectionId: selectionId, userId: userId, 
                        // we want to display a personalized message when an indexing error occurs :
                        axiosRequestConfig: { handleErrorLocally: true } }))];
                case 1:
                    response = _a.sent();
                    if (response.status !== 204) {
                        throw new IndexationException("http code \"".concat(response.status, "\" is incorrect during step \"").concat(IndexationStep.SEND_BULK_INDEXATION, "\""));
                    }
                    return [2 /*return*/];
            }
        });
    });
}
function importElementInWall(axiosInstance, indexationState, setIndexationState, setIndexationProgression, kpkMediaImports, allowedForms, formTypeFormIdMappings) {
    var _a;
    return __awaiter(this, void 0, void 0, function () {
        var mediaIds, descriptorsHref, needNewUploadErrors, error, mediaImportsCount, indexedMediaCount, _loop_1, _i, _b, kpkMediaImport;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    mediaIds = [];
                    descriptorsHref = [];
                    needNewUploadErrors = [];
                    error = false;
                    setIndexationProgression(0);
                    mediaImportsCount = kpkMediaImports.length;
                    indexedMediaCount = 0;
                    _loop_1 = function (kpkMediaImport) {
                        var formType, formId, response, stack_1;
                        return __generator(this, function (_d) {
                            switch (_d.label) {
                                case 0:
                                    formType = getFormTypeFromMediaImport(kpkMediaImport);
                                    formId = (_a = formTypeFormIdMappings === null || formTypeFormIdMappings === void 0 ? void 0 : formTypeFormIdMappings.find(function (mapping) { return mapping.formType === formType; })) === null || _a === void 0 ? void 0 : _a.formId;
                                    _d.label = 1;
                                case 1:
                                    _d.trys.push([1, 3, , 4]);
                                    return [4 /*yield*/, postMedia(axiosInstance, __assign(__assign({ fileUrl: kpkMediaImport.descriptorHref }, (formId && (allowedForms === null || allowedForms === void 0 ? void 0 : allowedForms.some(function (af) { return af.id === formId; })) && { formId: formId })), { 
                                            // we want to display a personalized message when an indexing error occurs :
                                            axiosRequestConfig: { handleErrorLocally: true } }))];
                                case 2:
                                    response = _d.sent();
                                    if (response.status === 201) {
                                        mediaIds.push(Number(KpkApiResponseUtils.extractEntityIdFromHref(response.headers.location)));
                                        descriptorsHref.push(kpkMediaImport.descriptorHref);
                                        indexedMediaCount += 1;
                                        setIndexationProgression((indexedMediaCount / mediaImportsCount) * 60);
                                    }
                                    else {
                                        // we don't throw an exception here because we have to go through the "setIndexationState" after the loop to save the correctly processed files
                                        error = true;
                                        console.error("http code \"".concat(response.status, "\" is incorrect during step \"").concat(IndexationStep.IMPORT_ELEMENT_IN_WALL, "\" on media href ").concat(kpkMediaImport.descriptorHref));
                                    }
                                    return [3 /*break*/, 4];
                                case 3:
                                    stack_1 = _d.sent();
                                    // we don't throw an exception here because we have to go through the "setIndexationState" after the loop to save the correctly processed files
                                    error = true;
                                    console.error("error during step \"".concat(IndexationStep.IMPORT_ELEMENT_IN_WALL, "\" on media href ").concat(kpkMediaImport.descriptorHref), stack_1);
                                    // this file descriptor is no longer available : do not continue to import it.
                                    // this can come from a connection cut just after the descriptor has been consumed without having had time to recover the media ID.
                                    if (isDescriptorAlreadyConsume(stack_1)) {
                                        descriptorsHref.push(kpkMediaImport.descriptorHref);
                                        needNewUploadErrors.push(kpkMediaImport.originalFileName);
                                    }
                                    return [3 /*break*/, 4];
                                case 4: return [2 /*return*/];
                            }
                        });
                    };
                    _i = 0, _b = kpkMediaImports.filter(function (mediaImport) {
                        return mediaImport.status === ImportStatus.SUCCESS &&
                            !indexationState.descriptorsHref.includes(mediaImport.descriptorHref);
                    });
                    _c.label = 1;
                case 1:
                    if (!(_i < _b.length)) return [3 /*break*/, 4];
                    kpkMediaImport = _b[_i];
                    return [5 /*yield**/, _loop_1(kpkMediaImport)];
                case 2:
                    _c.sent();
                    _c.label = 3;
                case 3:
                    _i++;
                    return [3 /*break*/, 1];
                case 4:
                    setIndexationState(function (old) {
                        return __assign(__assign({}, old), { error: error, step: error ? IndexationStep.IMPORT_ELEMENT_IN_WALL : IndexationStep.CREATE_USER_SELECTION, needNewUploadErrors: __spreadArray(__spreadArray([], old.needNewUploadErrors, true), needNewUploadErrors, true), mediaIds: __spreadArray(__spreadArray([], old.mediaIds, true), mediaIds, true), descriptorsHref: __spreadArray(__spreadArray([], old.descriptorsHref, true), descriptorsHref, true) });
                    });
                    return [2 /*return*/];
            }
        });
    });
}
function createUserSelection(axiosInstance, setIndexationState, userId) {
    return __awaiter(this, void 0, void 0, function () {
        var userSelection;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, postUserSelection(axiosInstance, {
                        userId: userId,
                        // we want to display a personalized message when an indexing error occurs :
                        axiosRequestConfig: { handleErrorLocally: true }
                    })];
                case 1:
                    userSelection = _a.sent();
                    if (userSelection.status !== 201) {
                        throw new IndexationException("http code \"".concat(userSelection.status, "\" is incorrect during step \"").concat(IndexationStep.CREATE_USER_SELECTION, "\""));
                    }
                    setIndexationState(function (old) {
                        return __assign(__assign({}, old), { step: IndexationStep.ADD_MEDIA_IN_SELECTION, selectionId: userSelection.data.id });
                    });
                    return [2 /*return*/];
            }
        });
    });
}
function addMediaInSelection(axiosInstance, indexationState, setIndexationState, userId) {
    return __awaiter(this, void 0, void 0, function () {
        var resp;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, postUserSelectionMediasCollection(axiosInstance, {
                        userId: userId,
                        selectionId: indexationState.selectionId,
                        mediaIds: indexationState.mediaIds,
                        // we want to display a personalized message when an indexing error occurs :
                        axiosRequestConfig: { handleErrorLocally: true }
                    })];
                case 1:
                    resp = _a.sent();
                    if (resp.status !== 201) {
                        throw new IndexationException("http code \"".concat(resp.status, "\" is incorrect during step \"").concat(IndexationStep.ADD_MEDIA_IN_SELECTION, "\""));
                    }
                    setIndexationState(function (old) {
                        return __assign(__assign({}, old), { step: IndexationStep.SEND_BULK_INDEXATION });
                    });
                    return [2 /*return*/];
            }
        });
    });
}
function sendBulkIndexation(axiosInstance, indexationState, setIndexationState, availableLocaleList, userId, indexInAllAvailableLocale) {
    return __awaiter(this, void 0, void 0, function () {
        var eligibleFieldsResponse, cleanedFormFields, selectionId, metadata, _i, availableLocaleList_1, locale;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, getUserSelectionBulkFields(axiosInstance, {
                        userId: userId,
                        selectionId: indexationState.selectionId,
                        // we want to display a personalized message when an indexing error occurs :
                        axiosRequestConfig: { handleErrorLocally: true }
                    })];
                case 1:
                    eligibleFieldsResponse = _a.sent();
                    if (eligibleFieldsResponse.status !== 200) {
                        throw new IndexationException("http code \"".concat(eligibleFieldsResponse.status, "\" is incorrect during step \"").concat(IndexationStep.SEND_BULK_INDEXATION, "\""));
                    }
                    cleanedFormFields = indexationState.formFields.filter(function (ff) { var _a; return (_a = eligibleFieldsResponse.data._embedded) === null || _a === void 0 ? void 0 : _a.field.some(function (ef) { return ef.id === ff.id; }); });
                    selectionId = indexationState.selectionId;
                    metadata = buildMetadataForIndexation(cleanedFormFields);
                    if (!indexInAllAvailableLocale) return [3 /*break*/, 6];
                    _i = 0, availableLocaleList_1 = availableLocaleList;
                    _a.label = 2;
                case 2:
                    if (!(_i < availableLocaleList_1.length)) return [3 /*break*/, 5];
                    locale = availableLocaleList_1[_i];
                    // need to create a custom axios instance for each locale with specific "Accept-Language" header :
                    axiosInstance.defaults.headers.common["Accept-Language"] = locale;
                    // do not parallelize API calls here because there will be malfunctions (server error 500, etc.)
                    return [4 /*yield*/, customPostUserSelectionBulk(axiosInstance, metadata, selectionId, userId)];
                case 3:
                    // do not parallelize API calls here because there will be malfunctions (server error 500, etc.)
                    _a.sent();
                    _a.label = 4;
                case 4:
                    _i++;
                    return [3 /*break*/, 2];
                case 5: return [3 /*break*/, 8];
                case 6: return [4 /*yield*/, customPostUserSelectionBulk(axiosInstance, metadata, selectionId, userId)];
                case 7:
                    _a.sent();
                    _a.label = 8;
                case 8:
                    setIndexationState(function (old) {
                        return __assign(__assign({}, old), { step: IndexationStep.CREATE_CLASSIFICATION });
                    });
                    return [2 /*return*/];
            }
        });
    });
}
function createClassification(axiosInstance, indexationState, setIndexationState, setIndexationProgression, classificationDestination, overrideClassificationStep) {
    var _a;
    return __awaiter(this, void 0, void 0, function () {
        var error, hasRightError, mediaIdsToClassify, mediaIdsClassified, response, folderId, mediaToClassifyCount, classifiedMediaCount, _i, mediaIdsToClassify_1, mediaId, response, stack_2;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    error = false;
                    hasRightError = false;
                    mediaIdsToClassify = indexationState.mediaIds.filter(function (mediaId) { return !indexationState.mediaIdsAlreadyClassified.includes(mediaId); });
                    mediaIdsClassified = [];
                    if (!overrideClassificationStep) return [3 /*break*/, 2];
                    return [4 /*yield*/, overrideClassificationStep(mediaIdsToClassify)];
                case 1:
                    _b.sent();
                    return [3 /*break*/, 10];
                case 2:
                    if (!(!classificationDestination ||
                        ClassificationDestinationType.CLASSIFICATION_VALIDATION_REQUEST ===
                            classificationDestination.type)) return [3 /*break*/, 4];
                    setIndexationProgression(95);
                    return [4 /*yield*/, postClassificationValidationRequest(axiosInstance, {
                            mediaIds: mediaIdsToClassify,
                            // we want to display a personalized message when an indexing error occurs :
                            axiosRequestConfig: { handleErrorLocally: true }
                        })];
                case 3:
                    response = _b.sent();
                    if (response.status !== 201) {
                        throw new IndexationException("http code \"".concat(response.status, "\" is incorrect during step \"").concat(IndexationStep.CREATE_CLASSIFICATION, "\""));
                    }
                    else {
                        setIndexationProgression(100);
                        mediaIdsClassified.push.apply(mediaIdsClassified, mediaIdsToClassify);
                    }
                    return [3 /*break*/, 10];
                case 4:
                    if (!(ClassificationDestinationType.FOLDER === classificationDestination.type)) return [3 /*break*/, 10];
                    folderId = (_a = classificationDestination.options) === null || _a === void 0 ? void 0 : _a.folderId;
                    if (!folderId) {
                        throw new IndexationException("folderId must be configured to index the item in a folder during step \"".concat(IndexationStep.CREATE_CLASSIFICATION, "\""));
                    }
                    mediaToClassifyCount = mediaIdsToClassify.length;
                    classifiedMediaCount = 0;
                    _i = 0, mediaIdsToClassify_1 = mediaIdsToClassify;
                    _b.label = 5;
                case 5:
                    if (!(_i < mediaIdsToClassify_1.length)) return [3 /*break*/, 10];
                    mediaId = mediaIdsToClassify_1[_i];
                    _b.label = 6;
                case 6:
                    _b.trys.push([6, 8, , 9]);
                    return [4 /*yield*/, addMediaToFolder(axiosInstance, {
                            mediaId: mediaId,
                            folderId: folderId,
                            // we want to display a personalized message when an indexing error occurs :
                            axiosRequestConfig: { handleErrorLocally: true }
                        })];
                case 7:
                    response = _b.sent();
                    if (response.status !== 201) {
                        // we don't throw an exception here because we have to go through the "setIndexationState" after the loop to save the correctly classified elements
                        error = true;
                        console.error("http code \"".concat(response.status, "\" is incorrect during step \"").concat(IndexationStep.CREATE_CLASSIFICATION, "\""));
                    }
                    else {
                        classifiedMediaCount += 1;
                        setIndexationProgression((classifiedMediaCount / mediaToClassifyCount) * 40 + 60);
                        mediaIdsClassified.push(mediaId);
                    }
                    return [3 /*break*/, 9];
                case 8:
                    stack_2 = _b.sent();
                    // we don't throw an exception here because we have to go through the "setIndexationState" after the loop to save the correctly classified elements
                    error = true;
                    if (isFolderRightError(stack_2)) {
                        hasRightError = true;
                    }
                    console.error("error during step \"".concat(IndexationStep.CREATE_CLASSIFICATION, "\" on element id ").concat(mediaId), stack_2);
                    return [3 /*break*/, 9];
                case 9:
                    _i++;
                    return [3 /*break*/, 5];
                case 10:
                    setIndexationState(function (old) {
                        return __assign(__assign({}, old), { error: error, hasRightError: hasRightError, mediaIdsAlreadyClassified: __spreadArray(__spreadArray([], old.mediaIdsAlreadyClassified, true), mediaIdsClassified, true), step: error ? IndexationStep.CREATE_CLASSIFICATION : IndexationStep.FINISHED });
                    });
                    return [2 /*return*/];
            }
        });
    });
}
function isDescriptorAlreadyConsume(error) {
    if (!error.response || !error.response.data || error.response.status !== 400) {
        return false;
    }
    return (error.response.data.message === "bad_request_file_descriptor_already_valid_file_descriptor");
}
function isFolderRightError(error) {
    if (!error.response || !error.response.data || error.response.status !== 403) {
        return false;
    }
    return error.response.data.message === "folder-updateDenied";
}

export { IndexationException, addMediaInSelection, buildMetadataForIndexation, createClassification, createCustomIndexationRetryAxiosInstance, createUserSelection, getFormTypeFromMediaImport, importElementInWall, sendBulkIndexation };
//# sourceMappingURL=utils.js.map
